import { gql, useQuery } from '@apollo/client';
import { BuildingLibraryIcon } from '@heroicons/react/24/outline';
import { BankAccountType } from '__generated__/graphql';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

import AddBankAccountDialogButton from './AddBankAccountDialogButton';
import UpdateBankAccountDialog from './UpdateBankAccountDialog';

const INVESTMENT_ENTITY_BANK_ACCOUNTS_QUERY = gql(`
  query InvestmentEntityBankAccountsInformation($id: ID!) {
    investmentEntity(id: $id) {
      id
      bankAccounts {
        id
        bankName
        accountHolderName
        accountType
        accountNumber
        ifscCode
        isVerified
      }
      permissions {
        canAddBankAccount
      }
    }
  }
`);

const InvestmentEntityBankAccount: FC = () => {
  const [selectedBankDetails, setSelectedBankDetails] = useState<BankAccountType | null>(null);

  const { investmentEntityId } = useParams<{ investmentEntityId: string }>() as {
    investmentEntityId: string;
  };
  const { loading, data, error, refetch } = useQuery(INVESTMENT_ENTITY_BANK_ACCOUNTS_QUERY, {
    variables: {
      id: investmentEntityId,
    },
  });

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;
  const bankAccounts = data.investmentEntity.bankAccounts;

  return (
    <>
      {selectedBankDetails && (
        <UpdateBankAccountDialog
          investmentEntityId={investmentEntityId}
          bankAccount={selectedBankDetails}
          onClose={() => setSelectedBankDetails(null)}
        />
      )}
      <div className="mb-4 flex flex-col gap-4 rounded-lg bg-indigo-50 p-5 ring-1 ring-indigo-100 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-white">
            <BuildingLibraryIcon className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <p className="text-base font-semibold text-gray-900">Bank accounts</p>
            <p className="mt-1 text-sm text-gray-600">
              The accounts linked to this investment entity. Select an account to view or update its
              details.
            </p>
          </div>
        </div>
        {data.investmentEntity.permissions.canAddBankAccount && (
          <div className="flex-shrink-0">
            <AddBankAccountDialogButton investmentEntityId={data.investmentEntity.id} />
          </div>
        )}
      </div>
      <DataTable
        data={bankAccounts}
        onClick={bankDetails => setSelectedBankDetails(bankDetails)}
        emptyListTitle="You haven't added any bank accounts yet."
        emptyListDescription="Add a bank account to get started."
        columns={[
          {
            label: 'Bank Name',
            fieldName: 'bankName',
          },
          {
            label: 'Account Holder Name',
            fieldName: 'accountHolderName',
          },
          {
            label: 'Account Number',
            fieldName: 'accountNumber',
          },
          {
            label: 'IFSC Code',
            fieldName: 'ifscCode',
          },
          {
            label: 'Account Type',
            fieldName: 'accountType',
          },
        ]}
      />
    </>
  );
};

export default InvestmentEntityBankAccount;
